import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('taptag_token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('taptag_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (emailOrUser, passwordOrToken) => {
    // If called with user object and token (from registration)
    if (typeof emailOrUser === 'object' && emailOrUser !== null) {
      const userData = emailOrUser;
      const accessToken = passwordOrToken;
      localStorage.setItem('taptag_token', accessToken);
      setToken(accessToken);
      setUser(userData);
      return userData;
    }
    
    // Normal login with email and password
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email: emailOrUser,
      password: passwordOrToken
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('taptag_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password, phone, acceptedPrivacy = true) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password,
      phone: phone || null,
      accepted_privacy: acceptedPrivacy
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('taptag_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('taptag_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
